import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Ship,
  Plus,
  Search,
  Save,
  Trash2,
  Edit2,
  ClipboardList,
  Package,
  Link2,
  ChevronLeft,
  Download,
  FileText,
  MapPin,
  Printer,
} from 'lucide-react';
import { TRACKING_STATUS_OPTIONS } from '../utils/packageTrack';
import { validateBulkTrackingForm } from '../utils/packageValidation';
import { buildPackingRowsFromLinkedCollections } from '../utils/shipmentPackingList';
import {
  downloadPackingListExcel,
  downloadPackingListPdf,
  printPackingListA4,
} from '../utils/shipmentPackingListExport';
import {
  buildCargoManifestRows,
  sumManifestTotals,
  buildManifestMeta,
  agentBlockFromDraft,
  MANIFEST_COMPANY,
  MANIFEST_LABEL,
  splitManifestAddressLines,
} from '../utils/shipmentCargoManifest';
import { downloadCargoManifestExcel, downloadCargoManifestPdf } from '../utils/shipmentCargoManifestExport';

function nowDateInput() {
  return new Date().toISOString().slice(0, 10);
}

function nowTimeInput() {
  const d = new Date();
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

const emptyManifestLine = () => ({
  marksAndNumbers: '',
  description: '',
  hsCode: '',
  noOfPackages: '',
  grossWeight: '',
  measurement: '',
});

const emptyPackingLine = () => ({
  cartonNo: '',
  description: '',
  quantity: '',
  netWeightKg: '',
  grossWeightKg: '',
  length: '',
  width: '',
  height: '',
});

const emptyShippingAgentRegistration = () => ({
  companyId: '',
  companyName: '',
  email: '',
  address: '',
  primaryMobile: '',
  secondaryMobile: '',
});

function agentDocToRegistration(a) {
  if (!a || typeof a !== 'object') return emptyShippingAgentRegistration();
  return {
    companyId: String(a.companyId ?? '').trim(),
    companyName: String(a.companyName ?? '').trim(),
    email: String(a.email ?? '').trim(),
    address: String(a.address ?? '').trim(),
    primaryMobile: String(a.primaryMobile ?? '').trim(),
    secondaryMobile: String(a.secondaryMobile ?? '').trim(),
  };
}

const emptyDraft = () => ({
  shipmentCode: '',
  shipmentDate: '',
  billOfLadingNo: '',
  containerNo: '',
  sealNo: '',
  vesselName: '',
  voyageNo: '',
  departureDate: '',
  arrivalDate: '',
  savedShippingAgentId: '',
  shippingAgentRegistration: emptyShippingAgentRegistration(),
  description: '',
  linkedCollections: [],
  manifest: [emptyManifestLine()],
  packingList: [emptyPackingLine()],
});

function readShippingAgentRegistration(ws) {
  const blank = emptyShippingAgentRegistration();
  const r = ws.shippingAgentRegistration;
  if (r && typeof r === 'object') {
    return {
      companyId: String(r.companyId ?? '').trim(),
      companyName: String(r.companyName ?? '').trim(),
      email: String(r.email ?? '').trim(),
      address: String(r.address ?? '').trim(),
      primaryMobile: String(r.primaryMobile ?? '').trim(),
      secondaryMobile: String(r.secondaryMobile ?? '').trim(),
    };
  }
  const legacy = String(ws.shippingAgent || '').trim();
  if (legacy) return { ...blank, companyName: legacy };
  return blank;
}

function toDateInput(v) {
  if (!v) return '';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return '';
  return d.toISOString().slice(0, 10);
}

function shipmentToDraft(s) {
  const ws = s.workspace || {};
  return {
    shipmentCode: ws.shipmentCode || '',
    shipmentDate: toDateInput(ws.shipmentDate),
    billOfLadingNo: ws.billOfLadingNo || '',
    containerNo: ws.containerNo || '',
    sealNo: ws.sealNo || '',
    vesselName: ws.vesselName || '',
    voyageNo: ws.voyageNo || '',
    departureDate: toDateInput(ws.departureDate),
    arrivalDate: toDateInput(ws.arrivalDate),
    savedShippingAgentId: ws.shippingAgentId ? String(ws.shippingAgentId) : '',
    shippingAgentRegistration: readShippingAgentRegistration(ws),
    description: ws.description || '',
    linkedCollections: (ws.linkedCollections || [])
      .map((c) => (typeof c === 'object' && c?._id ? String(c._id) : String(c)))
      .filter(Boolean),
    manifest: Array.isArray(ws.manifest) && ws.manifest.length ? ws.manifest.map((m) => ({
      marksAndNumbers: m.marksAndNumbers || '',
      description: m.description || '',
      hsCode: m.hsCode || '',
      noOfPackages: m.noOfPackages || '',
      grossWeight: m.grossWeight || '',
      measurement: m.measurement || '',
    })) : [emptyManifestLine()],
    packingList: Array.isArray(ws.packingList) && ws.packingList.length ? ws.packingList.map((p) => ({
      cartonNo: p.cartonNo || '',
      description: p.description || '',
      quantity: p.quantity || '',
      netWeightKg: p.netWeightKg || '',
      grossWeightKg: p.grossWeightKg || '',
      length: p.length || '',
      width: p.width || '',
      height: p.height || '',
    })) : [emptyPackingLine()],
  };
}

/**
 * Admin sea-freight shipments: details, manifest, packing list, linked collections.
 */
export default function AdminShipmentsWorkspace({
  apiBase,
  authToken,
  collections,
  loadingCollections,
  shippersList = [],
  consigneesList = [],
  isActive,
  getStatusColor,
  getStatusIcon,
  onManageShippingAgents,
}) {
  const [shipments, setShipments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');
  const [panelTab, setPanelTab] = useState('details');
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState(emptyDraft);
  const [savedShippingAgents, setSavedShippingAgents] = useState([]);
  const [linkCollectionsSearch, setLinkCollectionsSearch] = useState('');
  const [linkCollectionsAssignment, setLinkCollectionsAssignment] = useState('all');
  const [linkCollectionsClearing, setLinkCollectionsClearing] = useState('all');
  const [trackStatus, setTrackStatus] = useState('malaysia-warehouse');
  const [trackDate, setTrackDate] = useState(nowDateInput);
  const [trackTime, setTrackTime] = useState(nowTimeInput);
  const [trackDescription, setTrackDescription] = useState('');
  const [trackBusy, setTrackBusy] = useState(false);
  const [trackMsg, setTrackMsg] = useState('');

  const loadShipments = useCallback(async () => {
    if (!authToken) return;
    setLoading(true);
    try {
      const res = await fetch(`${apiBase}/api/shipments?limit=200`, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) {
        console.error('Shipments load failed', json);
        setShipments([]);
        return;
      }
      setShipments(Array.isArray(json.data) ? json.data : []);
    } catch (e) {
      console.error(e);
      setShipments([]);
    } finally {
      setLoading(false);
    }
  }, [apiBase, authToken]);

  const loadShippingAgents = useCallback(async () => {
    if (!authToken) return;
    try {
      const res = await fetch(`${apiBase}/api/shipping-agents`, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      const json = await res.json().catch(() => ({}));
      if (res.ok && Array.isArray(json.data)) {
        setSavedShippingAgents(json.data);
      } else {
        setSavedShippingAgents([]);
      }
    } catch {
      setSavedShippingAgents([]);
    }
  }, [apiBase, authToken]);

  useEffect(() => {
    if (isActive && authToken) {
      loadShipments();
      loadShippingAgents();
    }
  }, [isActive, authToken, loadShipments, loadShippingAgents]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return shipments;
    return shipments.filter((s) => {
      const ws = s.workspace || {};
      const code = (ws.shipmentCode || s.trackingNumber || '').toLowerCase();
      const bl = (ws.billOfLadingNo || '').toLowerCase();
      const vessel = (ws.vesselName || '').toLowerCase();
      const cust = (s.customer?.name || '').toLowerCase();
      return code.includes(q) || bl.includes(q) || vessel.includes(q) || cust.includes(q);
    });
  }, [shipments, search]);

  const collectionById = useMemo(() => {
    const m = new Map();
    (collections || []).forEach((c) => {
      const id = c._id || c.id;
      if (id) m.set(String(id), c);
    });
    return m;
  }, [collections]);

  const collectionIdsLinkedAnywhere = useMemo(() => {
    const set = new Set();
    shipments.forEach((s) => {
      (s.workspace?.linkedCollections || []).forEach((id) => {
        if (id != null) set.add(String(id));
      });
    });
    return set;
  }, [shipments]);

  const collectionIdsLinkedOtherShipments = useMemo(() => {
    const set = new Set();
    const cur = editingId && editingId !== 'new' ? String(editingId) : null;
    shipments.forEach((s) => {
      if (cur && String(s._id) === cur) return;
      (s.workspace?.linkedCollections || []).forEach((id) => {
        if (id != null) set.add(String(id));
      });
    });
    return set;
  }, [shipments, editingId]);

  const pickableCollections = useMemo(() => {
    const list = collections || [];
    const q = linkCollectionsSearch.trim().toLowerCase();
    const draftLinked = new Set(draft.linkedCollections.map(String));

    const passesSearch = (c) => {
      if (!q) return true;
      const id = String(c._id || c.id);
      const blob = [c.collectionNo, c.consigneeId, c.clearingType, c.shipperPassportNo, id]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return blob.includes(q);
    };

    const passesClearing = (c) => {
      if (linkCollectionsClearing === 'all') return true;
      const ct = String(c.clearingType || '')
        .toLowerCase()
        .replace(/\s+/g, '_');
      return ct === linkCollectionsClearing;
    };

    const passesAssignment = (c) => {
      const id = String(c._id || c.id);
      if (draftLinked.has(id)) return true;
      switch (linkCollectionsAssignment) {
        case 'all':
          return true;
        case 'unassigned':
          return !collectionIdsLinkedAnywhere.has(id);
        case 'on_this':
          return false;
        case 'other_shipment':
          return collectionIdsLinkedOtherShipments.has(id);
        default:
          return true;
      }
    };

    return list
      .filter((c) => {
        if (c.consolidatedInto) return false;
        return passesSearch(c) && passesClearing(c) && passesAssignment(c);
      })
      .sort((a, b) => {
        const ida = String(a._id || a.id);
        const idb = String(b._id || b.id);
        const da = draftLinked.has(ida) ? 0 : 1;
        const db = draftLinked.has(idb) ? 0 : 1;
        if (da !== db) return da - db;
        return String(a.collectionNo || ida).localeCompare(String(b.collectionNo || idb), undefined, {
          numeric: true,
        });
      });
  }, [
    collections,
    linkCollectionsSearch,
    linkCollectionsAssignment,
    linkCollectionsClearing,
    draft.linkedCollections,
    collectionIdsLinkedAnywhere,
    collectionIdsLinkedOtherShipments,
  ]);

  const packingRows = useMemo(
    () => buildPackingRowsFromLinkedCollections(draft.linkedCollections, collectionById),
    [draft.linkedCollections, collectionById],
  );

  const shipperByPassport = useMemo(() => {
    const m = new Map();
    (shippersList || []).forEach((s) => {
      const p = String(s.passportNo || '').trim();
      if (p) m.set(p, s);
    });
    return m;
  }, [shippersList]);

  const manifestRows = useMemo(
    () =>
      buildCargoManifestRows(
        draft.linkedCollections,
        collectionById,
        shipperByPassport,
        consigneesList || [],
      ),
    [draft.linkedCollections, collectionById, shipperByPassport, consigneesList],
  );

  const resolvedShippingAgentRegistration = useMemo(() => {
    const id = draft.savedShippingAgentId;
    if (id && savedShippingAgents.length) {
      const a = savedShippingAgents.find((x) => String(x._id) === String(id));
      if (a) return agentDocToRegistration(a);
    }
    return draft.shippingAgentRegistration;
  }, [draft.savedShippingAgentId, draft.shippingAgentRegistration, savedShippingAgents]);

  const draftForManifest = useMemo(
    () => ({ ...draft, shippingAgentRegistration: resolvedShippingAgentRegistration }),
    [draft, resolvedShippingAgentRegistration],
  );

  const manifestMeta = useMemo(() => {
    const totals = sumManifestTotals(manifestRows);
    return buildManifestMeta(draftForManifest, totals);
  }, [draftForManifest, manifestRows]);

  const manifestAgentBlock = useMemo(() => agentBlockFromDraft(draftForManifest), [draftForManifest]);

  const currentShipment = useMemo(() => {
    if (!editingId || editingId === 'new') return null;
    return shipments.find((s) => String(s._id) === String(editingId)) ?? null;
  }, [editingId, shipments]);

  const startNew = useCallback(() => {
    setEditingId('new');
    setDraft(emptyDraft());
    setPanelTab('details');
  }, []);

  const startEdit = (s, initialPanel = 'details') => {
    setEditingId(s._id);
    setDraft(shipmentToDraft(s));
    setPanelTab(initialPanel);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setDraft(emptyDraft());
  };

  const persistWorkspacePayload = () => {
    const id = /^[0-9a-fA-F]{24}$/.test(String(draft.savedShippingAgentId || ''))
      ? draft.savedShippingAgentId
      : null;
    let reg = emptyShippingAgentRegistration();
    if (id) {
      const a = savedShippingAgents.find((x) => String(x._id) === String(id));
      if (a) reg = agentDocToRegistration(a);
      else reg = { ...draft.shippingAgentRegistration };
    }
    return {
      shipmentCode: draft.shipmentCode.trim(),
      shipmentDate: draft.shipmentDate || undefined,
      billOfLadingNo: draft.billOfLadingNo.trim(),
      containerNo: draft.containerNo.trim(),
      sealNo: draft.sealNo.trim(),
      vesselName: draft.vesselName.trim(),
      voyageNo: draft.voyageNo.trim(),
      departureDate: draft.departureDate || undefined,
      arrivalDate: draft.arrivalDate || undefined,
      shippingAgentRegistration: reg,
      shippingAgentId: id || null,
      description: draft.description.trim(),
      linkedCollections: draft.linkedCollections,
      manifest: [],
      packingList: [],
    };
  };

  const alertApiError = (res, json, fallback) => {
    const msg = json?.message || fallback;
    if (res.status === 404 && /route not found/i.test(String(msg))) {
      alert(
        'Route not found — the API on this machine is almost certainly an old Node process without the workspace route.\n\n' +
          'In the BACKEND folder: stop the server (Ctrl+C), then run npm start (or npm run dev so code changes reload).\n\n' +
          `Calling: ${apiBase}`
      );
      return;
    }
    alert(msg);
  };

  const handleSave = async () => {
    if (!authToken) return;
    if (!draft.shipmentDate?.trim()) {
      alert('Please set shipment date.');
      setPanelTab('details');
      return;
    }
    setSaving(true);
    try {
      const workspace = persistWorkspacePayload();
      if (editingId === 'new') {
        const createUrls = [
          `${apiBase}/api/shipments/workspace`,
          `${apiBase}/api/admin/shipments/workspace`,
        ];
        let res;
        let json = {};
        for (const url of createUrls) {
          res = await fetch(url, {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${authToken}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ workspace }),
          });
          json = await res.json().catch(() => ({}));
          if (res.ok) break;
          const isRouteMissing =
            res.status === 404 && /route not found/i.test(String(json.message || ''));
          if (!isRouteMissing) break;
        }
        if (!res.ok) {
          alertApiError(res, json, 'Could not create shipment');
          return;
        }
        await loadShipments();
        if (json.data?._id) {
          setEditingId(json.data._id);
          setDraft(shipmentToDraft(json.data));
        } else {
          cancelEdit();
        }
      } else if (editingId) {
        const res = await fetch(`${apiBase}/api/shipments/${editingId}`, {
          method: 'PUT',
          headers: {
            Authorization: `Bearer ${authToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ workspace }),
        });
        const json = await res.json().catch(() => ({}));
        if (!res.ok) {
          alert(json.message || 'Could not update shipment');
          return;
        }
        await loadShipments();
        if (json.data) setDraft(shipmentToDraft(json.data));
      }
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!authToken || !window.confirm('Delete this shipment?')) return;
    try {
      const res = await fetch(`${apiBase}/api/shipments/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${authToken}` },
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        alert(j.message || 'Delete failed (admin only)');
        return;
      }
      if (editingId === id) cancelEdit();
      await loadShipments();
    } catch (e) {
      alert(e.message || 'Delete failed');
    }
  };

  const handleBulkTracking = async () => {
    if (!authToken || !editingId || editingId === 'new') {
      alert('Save the shipment first, then apply tracking updates.');
      return;
    }
    if (!draft.linkedCollections.length) {
      alert('Link at least one HBL collection on the Collections tab first.');
      return;
    }
    const validation = validateBulkTrackingForm({
      status: trackStatus,
      eventDate: trackDate,
      eventTime: trackTime,
    });
    if (!validation.ok) {
      setTrackMsg(validation.message);
      return;
    }
    setTrackBusy(true);
    setTrackMsg('');
    try {
      const res = await fetch(`${apiBase}/api/shipments/${editingId}/tracking/bulk`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authToken}`,
        },
        body: JSON.stringify({
          status: trackStatus,
          eventDate: trackDate,
          eventTime: trackTime,
          description: trackDescription.trim() || undefined,
        }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.message || 'Tracking update failed');
      setTrackMsg(json.message || 'Tracking updated for all linked HBLs.');
      await loadShipments();
    } catch (e) {
      setTrackMsg(e.message || 'Tracking update failed');
    } finally {
      setTrackBusy(false);
    }
  };

  const toggleCollection = (cid) => {
    const id = String(cid);
    setDraft((d) => {
      const set = new Set(d.linkedCollections.map(String));
      if (set.has(id)) set.delete(id);
      else set.add(id);
      return { ...d, linkedCollections: [...set] };
    });
  };

  const statusLabel = (s) => String(s.status || 'pending').replace(/-/g, ' ');

  const applySavedShippingAgent = (id) => {
    if (!id) {
      setDraft((d) => ({
        ...d,
        savedShippingAgentId: '',
        shippingAgentRegistration: emptyShippingAgentRegistration(),
      }));
      return;
    }
    const a = savedShippingAgents.find((x) => String(x._id) === id);
    if (!a) return;
    setDraft((d) => ({
      ...d,
      savedShippingAgentId: id,
      shippingAgentRegistration: agentDocToRegistration(a),
    }));
  };

  const agentSelectOptions = savedShippingAgents.map((a) => (
    <option key={a._id} value={String(a._id)}>
      {a.companyName || a.companyId || String(a._id).slice(-6)}
    </option>
  ));

  return (
    <div>
      <div className="jet-card" style={{ marginBottom: '1rem' }}>
        <div className="jet-admin-filter-row" style={{ marginBottom: 0 }}>
          <div className="jet-admin-filter-search">
            <Search
              className="w-4 h-4"
              style={{
                position: 'absolute',
                left: '12px',
                top: '50%',
                transform: 'translateY(-50%)',
                color: 'var(--jet-muted)',
                pointerEvents: 'none',
              }}
            />
            <input
              type="search"
              className="form-input"
              placeholder="Search by shipment code, tracking, B/L, vessel…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ paddingLeft: '40px' }}
              aria-label="Search shipments"
            />
          </div>
          <button type="button" className="jet-btn jet-btn-primary" onClick={startNew}>
            <Plus size={16} />
            Add shipment
          </button>
          <button type="button" className="jet-btn jet-btn-secondary" onClick={loadShipments} disabled={loading}>
            Refresh
          </button>
        </div>
      </div>

      <div className={`jet-admin-shipments-layout${editingId ? ' jet-admin-shipments-layout--split' : ''}`}>
        <div className="jet-card jet-admin-shipments-list">
          <h2 className="jet-table-title" style={{ margin: '0 0 1rem' }}>
            Shipments ({filtered.length}
            {filtered.length !== shipments.length ? ` of ${shipments.length}` : ''})
          </h2>
          {editingId && (
            <p
              style={{
                margin: '-0.5rem 0 1rem',
                fontSize: '0.8125rem',
                color: 'var(--jet-muted)',
              }}
            >
              List is shortened while you edit — scroll inside the box to pick another shipment, or use Back to list.
            </p>
          )}
          {loading ? (
            <p style={{ color: 'var(--jet-muted)' }}>Loading…</p>
          ) : filtered.length === 0 ? (
            <p style={{ color: 'var(--jet-muted)' }}>No shipments yet. Add one or adjust search.</p>
          ) : (
            <div className="jet-table-wrap">
              <table className="jet-table">
                <thead>
                  <tr>
                    <th>Code</th>
                    <th>Tracking</th>
                    <th>Date</th>
                    <th>Vessel</th>
                    <th>Collections</th>
                    <th>Manifest</th>
                    <th>Packing list</th>
                    <th>Status</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((s) => {
                    const ws = s.workspace || {};
                    const n = (ws.linkedCollections || []).length;
                    return (
                      <tr key={s._id}>
                        <td style={{ fontWeight: 600 }}>{ws.shipmentCode || '—'}</td>
                        <td>{s.trackingNumber}</td>
                        <td>{ws.shipmentDate ? toDateInput(ws.shipmentDate) : '—'}</td>
                        <td>{ws.vesselName || '—'}</td>
                        <td>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', flexWrap: 'wrap' }}>
                            <span style={{ fontVariantNumeric: 'tabular-nums' }}>{n}</span>
                            <button
                              type="button"
                              className="jet-btn jet-btn-secondary jet-btn-icon"
                              title="Open collections"
                              aria-label={`Open collections for ${ws.shipmentCode || s.trackingNumber}`}
                              onClick={() => startEdit(s, 'collections')}
                            >
                              <Link2 size={14} />
                            </button>
                          </div>
                        </td>
                        <td>
                          <button
                            type="button"
                            className="jet-btn jet-btn-secondary jet-btn-icon"
                            title="Open manifest"
                            aria-label={`Open manifest for ${ws.shipmentCode || s.trackingNumber}`}
                            onClick={() => startEdit(s, 'manifest')}
                          >
                            <ClipboardList size={14} />
                          </button>
                        </td>
                        <td>
                          <button
                            type="button"
                            className="jet-btn jet-btn-secondary jet-btn-icon"
                            title="Open packing list"
                            aria-label={`Open packing list for ${ws.shipmentCode || s.trackingNumber}`}
                            onClick={() => startEdit(s, 'packing')}
                          >
                            <Package size={14} />
                          </button>
                        </td>
                        <td>
                          <span
                            className={`px-2 py-1 rounded-full text-xs font-medium inline-flex items-center gap-1 w-fit ${getStatusColor(statusLabel(s))}`}
                          >
                            {getStatusIcon(statusLabel(s))}
                            {statusLabel(s)}
                          </span>
                        </td>
                        <td>
                          <div style={{ display: 'flex', gap: '0.35rem', flexWrap: 'wrap' }}>
                            <button
                              type="button"
                              className="jet-btn jet-btn-secondary jet-btn-icon"
                              title="Edit shipment details"
                              onClick={() => startEdit(s, 'details')}
                            >
                              <Edit2 size={14} />
                            </button>
                            <button
                              type="button"
                              className="jet-btn jet-btn-danger jet-btn-icon"
                              title="Delete"
                              onClick={() => handleDelete(s._id)}
                            >
                              <Trash2 size={14} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {editingId && (
          <div className="jet-card jet-admin-shipments-editor">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '0.75rem', flexWrap: 'wrap', marginBottom: '1rem' }}>
              <h2 style={{ margin: 0, fontSize: '1.05rem', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <Ship className="w-5 h-5" style={{ color: 'var(--jet-blue)' }} aria-hidden />
                {editingId === 'new' ? 'New shipment' : 'Edit shipment'}
              </h2>
              <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                <button type="button" className="jet-btn jet-btn-primary" onClick={handleSave} disabled={saving}>
                  <Save size={16} />
                  {saving ? 'Saving…' : 'Save'}
                </button>
                <button type="button" className="jet-btn jet-btn-secondary" onClick={cancelEdit}>
                  <ChevronLeft size={16} aria-hidden />
                  Back to list
                </button>
              </div>
            </div>

            <div className="jet-shipment-subtabs" role="tablist">
              {[
                { id: 'details', label: 'Shipment details', Icon: Ship },
                { id: 'manifest', label: 'Manifest', Icon: ClipboardList },
                { id: 'packing', label: 'Packing list', Icon: Package },
                { id: 'collections', label: 'Collections', Icon: Link2 },
                { id: 'tracking', label: 'HBL tracking', Icon: MapPin },
              ].map(({ id, label, Icon }) => (
                <button
                  key={id}
                  type="button"
                  role="tab"
                  aria-selected={panelTab === id}
                  className={`jet-shipment-subtab ${panelTab === id ? 'jet-shipment-subtab--active' : ''}`}
                  onClick={() => setPanelTab(id)}
                >
                  <Icon className="w-4 h-4" />
                  {label}
                </button>
              ))}
            </div>

            {panelTab === 'details' && (
              <div style={{ marginTop: '1.25rem' }}>
                <h3 style={{ fontSize: '0.95rem', fontWeight: 600, margin: '0 0 1rem', color: 'var(--jet-text)' }}>
                  Shipment details
                </h3>
                <div className="jet-shipment-form-grid">
                  <div>
                    <label className="jet-shipment-label">Shipment ID</label>
                    <input
                      className="form-input"
                      value={draft.shipmentCode}
                      onChange={(e) => setDraft((d) => ({ ...d, shipmentCode: e.target.value }))}
                      placeholder="Auto if empty on create"
                    />
                  </div>
                  <div>
                    <label className="jet-shipment-label">Shipment date *</label>
                    <input
                      type="date"
                      className="form-input"
                      value={draft.shipmentDate}
                      onChange={(e) => setDraft((d) => ({ ...d, shipmentDate: e.target.value }))}
                    />
                  </div>
                  <div>
                    <label className="jet-shipment-label">Bill of Lading No (optional)</label>
                    <input
                      className="form-input"
                      value={draft.billOfLadingNo}
                      onChange={(e) => setDraft((d) => ({ ...d, billOfLadingNo: e.target.value }))}
                      placeholder="Enter Bill of Lading number"
                    />
                  </div>
                  <div>
                    <label className="jet-shipment-label">Container No (optional)</label>
                    <input
                      className="form-input"
                      value={draft.containerNo}
                      onChange={(e) => setDraft((d) => ({ ...d, containerNo: e.target.value }))}
                      placeholder="Enter container number"
                    />
                  </div>
                  <div>
                    <label className="jet-shipment-label">Seal No (optional)</label>
                    <input
                      className="form-input"
                      value={draft.sealNo}
                      onChange={(e) => setDraft((d) => ({ ...d, sealNo: e.target.value }))}
                      placeholder="Enter seal number"
                    />
                  </div>
                  <div>
                    <label className="jet-shipment-label">Vessel name (optional)</label>
                    <input
                      className="form-input"
                      value={draft.vesselName}
                      onChange={(e) => setDraft((d) => ({ ...d, vesselName: e.target.value }))}
                      placeholder="Enter vessel name"
                    />
                  </div>
                  <div>
                    <label className="jet-shipment-label">Voyage No (optional)</label>
                    <input
                      className="form-input"
                      value={draft.voyageNo}
                      onChange={(e) => setDraft((d) => ({ ...d, voyageNo: e.target.value }))}
                      placeholder="Enter voyage number"
                    />
                  </div>
                  <div>
                    <label className="jet-shipment-label">Departure date (optional)</label>
                    <input
                      type="date"
                      className="form-input"
                      value={draft.departureDate}
                      onChange={(e) => setDraft((d) => ({ ...d, departureDate: e.target.value }))}
                    />
                  </div>
                  <div>
                    <label className="jet-shipment-label">Arrival date (optional)</label>
                    <input
                      type="date"
                      className="form-input"
                      value={draft.arrivalDate}
                      onChange={(e) => setDraft((d) => ({ ...d, arrivalDate: e.target.value }))}
                    />
                  </div>
                  <div className="jet-shipment-form-span-full">
                    <div
                      className="jet-shipping-agent-subsection"
                      style={{
                        marginTop: '0.25rem',
                        paddingTop: '1rem',
                        borderTop: '1px solid var(--jet-border)',
                      }}
                    >
                      <h4
                        style={{
                          margin: '0 0 0.85rem',
                          fontSize: '0.9375rem',
                          fontWeight: 700,
                          color: 'var(--jet-text)',
                        }}
                      >
                        Shipping agent
                      </h4>
                      <div
                        className="jet-admin-filter-row"
                        style={{ marginBottom: 0, flexWrap: 'wrap', gap: '0.5rem', alignItems: 'flex-end' }}
                      >
                        <div style={{ flex: '1 1 220px', minWidth: '200px' }}>
                          <label className="jet-shipment-label">Shipping agent name</label>
                          <select
                            className="form-input"
                            value={draft.savedShippingAgentId}
                            onChange={(e) => applySavedShippingAgent(e.target.value)}
                            aria-label="Shipping agent from directory"
                          >
                            <option value="">— None —</option>
                            {agentSelectOptions}
                          </select>
                        </div>
                        {typeof onManageShippingAgents === 'function' ? (
                          <button type="button" className="jet-btn jet-btn-secondary" onClick={onManageShippingAgents}>
                            Manage shipping agents
                          </button>
                        ) : null}
                      </div>
                      <p style={{ margin: '0.5rem 0 0', fontSize: '0.75rem', color: 'var(--jet-muted)' }}>
                        Full contact details are edited under <strong>Shipping agents</strong> in the sidebar. Manifests
                        use the saved record for this shipment.
                      </p>
                    </div>
                  </div>
                  <div className="jet-shipment-form-span-full">
                    <label className="jet-shipment-label">Description (optional)</label>
                    <textarea
                      className="form-textarea"
                      rows={4}
                      value={draft.description}
                      onChange={(e) => setDraft((d) => ({ ...d, description: e.target.value }))}
                      placeholder="Enter shipment description…"
                    />
                  </div>
                </div>
              </div>
            )}

            {panelTab === 'manifest' && (
              <div style={{ marginTop: '1.25rem' }}>
                <div
                  className="jet-manifest-letterhead"
                  style={{
                    marginBottom: '1rem',
                    padding: '1rem',
                    border: '1px solid var(--jet-border)',
                    borderRadius: 'var(--jet-radius-sm)',
                    background: 'var(--jet-bg)',
                  }}
                >
                  <h3
                    style={{
                      margin: '0 0 0.75rem',
                      fontSize: '1.05rem',
                      fontWeight: 800,
                      textAlign: 'center',
                      letterSpacing: '0.04em',
                    }}
                  >
                    CARGO MANIFEST
                  </h3>
                  <div className="jet-manifest-letterhead-grid">
                    <div className="jet-manifest-letterhead-col">
                      <div className="jet-manifest-letterhead-name">{MANIFEST_COMPANY.name}</div>
                      <div className="jet-manifest-letterhead-lines">
                        {splitManifestAddressLines(MANIFEST_COMPANY.address).map((line, i) => (
                          <div key={`sh-addr-${i}`}>{line.toUpperCase()}</div>
                        ))}
                        <div>
                          {MANIFEST_LABEL.email} {MANIFEST_COMPANY.email}
                        </div>
                        <div>
                          {MANIFEST_LABEL.tel} {MANIFEST_COMPANY.tel.toUpperCase()}
                        </div>
                      </div>
                    </div>
                    <div className="jet-manifest-letterhead-col">
                      <div className="jet-manifest-right-rail">
                        <div className="jet-manifest-letterhead-name">{manifestAgentBlock.name}</div>
                        <div className="jet-manifest-letterhead-lines">
                          {splitManifestAddressLines(manifestAgentBlock.address).map((line, i) => (
                            <div key={`ag-addr-${i}`}>{line.toUpperCase()}</div>
                          ))}
                          {manifestAgentBlock.emailLine ? <div>{manifestAgentBlock.emailLine}</div> : null}
                          {manifestAgentBlock.telLine ? (
                            <div>{manifestAgentBlock.telLine.toUpperCase()}</div>
                          ) : null}
                          {!manifestAgentBlock.emailLine &&
                          !manifestAgentBlock.telLine &&
                          manifestAgentBlock.row3Right &&
                          manifestAgentBlock.row3Right !== '—' ? (
                            <div style={{ whiteSpace: 'pre-line' }}>{manifestAgentBlock.row3Right}</div>
                          ) : null}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="jet-manifest-meta-grid jet-manifest-meta-ledger" style={{ marginTop: '0.85rem' }}>
                    <div className="jet-manifest-meta-col">
                      <div>
                        BILL OF LADING NO: <strong>{manifestMeta.billOfLadingNo}</strong>
                      </div>
                      <div>
                        VESSEL: <strong>{manifestMeta.vessel}</strong>
                      </div>
                      <div>
                        CONTAINER NO: <strong>{manifestMeta.container}</strong>
                      </div>
                    </div>
                    <div className="jet-manifest-meta-col jet-manifest-meta-col-totals">
                      <div className="jet-manifest-right-rail">
                        <div>
                          TOTAL PACKAGES: <strong>{manifestMeta.totalPackages}</strong>
                        </div>
                        <div>
                          TOTAL CBM: <strong>{manifestMeta.totalCbm}</strong>
                        </div>
                        <div>
                          TOTAL WEIGHT: <strong>{manifestMeta.totalWeightKg} KG</strong>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="jet-table-toolbar" style={{ marginBottom: '0.75rem', flexWrap: 'wrap', gap: '0.5rem' }}>
                  <div>
                    <h3 style={{ margin: 0, fontSize: '0.95rem', fontWeight: 600 }}>Cargo manifest (from linked collections)</h3>
                    <p style={{ margin: '0.35rem 0 0', fontSize: '0.8125rem', color: 'var(--jet-muted)', maxWidth: '44rem' }}>
                      One row per linked collection. Shipper/consignee come from master data (passport / consignee ID on the
                      collection). Edit B/L, vessel, and container on <strong>Shipment details</strong>.
                    </p>
                  </div>
                  <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                    <button
                      type="button"
                      className="jet-btn jet-btn-secondary"
                      disabled={!manifestRows.length}
                      onClick={() => {
                        downloadCargoManifestExcel({
                          rows: manifestRows,
                          draft: draftForManifest,
                          meta: manifestMeta,
                          shipmentCode: draft.shipmentCode,
                          trackingNumber: currentShipment?.trackingNumber,
                        });
                      }}
                    >
                      <Download size={16} />
                      Excel (.xlsx)
                    </button>
                    <button
                      type="button"
                      className="jet-btn jet-btn-secondary"
                      disabled={!manifestRows.length}
                      onClick={() => {
                        downloadCargoManifestPdf({
                          rows: manifestRows,
                          draft: draftForManifest,
                          meta: manifestMeta,
                          shipmentCode: draft.shipmentCode,
                          trackingNumber: currentShipment?.trackingNumber,
                        });
                      }}
                    >
                      <FileText size={16} />
                      PDF (A4 landscape)
                    </button>
                  </div>
                </div>

                {!draft.linkedCollections.length ? (
                  <p style={{ color: 'var(--jet-muted)' }}>Link collections on the Collections tab to build this manifest.</p>
                ) : manifestRows.length === 0 ? (
                  <p style={{ color: 'var(--jet-muted)' }}>No matching collection records found.</p>
                ) : (
                  <div className="jet-packing-table-wrap jet-manifest-table-wrap">
                    <table className="jet-packing-table jet-manifest-data-table">
                      <thead>
                        <tr>
                          <th>NO</th>
                          <th>HBL</th>
                          <th>SHIPPER DETAILS</th>
                          <th>CONSIGNEE DETAILS</th>
                          <th>PCS</th>
                          <th>CBM</th>
                          <th>WEIGHT</th>
                          <th>DESCRIPTION</th>
                          <th>UPB/GIFT</th>
                        </tr>
                      </thead>
                      <tbody>
                        {manifestRows.map((r) => (
                          <tr key={r.key}>
                            <td className="jet-packing-num">{r.no}</td>
                            <td>{r.hbl || '—'}</td>
                            <td className="jet-packing-desc" style={{ whiteSpace: 'pre-line' }}>
                              {r.shipperDetails}
                            </td>
                            <td className="jet-packing-desc" style={{ whiteSpace: 'pre-line' }}>
                              {r.consigneeDetails}
                            </td>
                            <td>{r.pcs}</td>
                            <td>{(Math.round(r.cbm * 100) / 100).toFixed(2)}</td>
                            <td>{Math.round(r.weight) || r.weight}</td>
                            <td className="jet-packing-desc" style={{ whiteSpace: 'pre-line' }}>
                              {r.description}
                            </td>
                            <td>{r.upbGift}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {panelTab === 'packing' && (
              <div style={{ marginTop: '1.25rem' }}>
                <div className="jet-table-toolbar" style={{ marginBottom: '0.75rem', flexWrap: 'wrap', gap: '0.5rem' }}>
                  <div>
                    <h3 style={{ margin: 0, fontSize: '0.95rem', fontWeight: 600 }}>Packing list</h3>
                    <p style={{ margin: '0.35rem 0 0', fontSize: '0.8125rem', color: 'var(--jet-muted)', maxWidth: '42rem' }}>
                      Rows come only from collections linked on this shipment (Collections tab). Edit boxes on each collection in Cargo admin.
                    </p>
                  </div>
                  <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                    <button
                      type="button"
                      className="jet-btn jet-btn-secondary"
                      disabled={!packingRows.length}
                      onClick={() => {
                        downloadPackingListExcel({
                          rows: packingRows,
                          shipmentCode: draft.shipmentCode,
                          trackingNumber: currentShipment?.trackingNumber,
                        });
                      }}
                    >
                      <Download size={16} />
                      Excel (.xlsx)
                    </button>
                    <button
                      type="button"
                      className="jet-btn jet-btn-secondary"
                      disabled={!packingRows.length}
                      onClick={() => {
                        downloadPackingListPdf({
                          rows: packingRows,
                          shipmentCode: draft.shipmentCode,
                          trackingNumber: currentShipment?.trackingNumber,
                        });
                      }}
                    >
                      <FileText size={16} />
                      PDF (A4 portrait)
                    </button>
                    <button
                      type="button"
                      className="jet-btn jet-btn-secondary"
                      disabled={!packingRows.length}
                      onClick={() => {
                        printPackingListA4({
                          rows: packingRows,
                          shipmentCode: draft.shipmentCode,
                          trackingNumber: currentShipment?.trackingNumber,
                        });
                      }}
                    >
                      <Printer size={16} />
                      Print A4
                    </button>
                  </div>
                </div>
                {!draft.linkedCollections.length ? (
                  <p style={{ color: 'var(--jet-muted)' }}>Link at least one collection to build a packing list.</p>
                ) : packingRows.length === 0 ? (
                  <p style={{ color: 'var(--jet-muted)' }}>Linked collections have no box lines yet.</p>
                ) : (
                  <div className="jet-packing-table-wrap">
                    <table className="jet-packing-table">
                      <thead>
                        <tr>
                          <th>NO</th>
                          <th>SUB NO</th>
                          <th>LENGTH</th>
                          <th>WIDTH</th>
                          <th>HEIGHT</th>
                          <th>KG</th>
                          <th>CBM</th>
                          <th>CARGO TYPE</th>
                          <th>DESCRIPTION</th>
                          <th>D2D</th>
                          <th style={{ textAlign: 'center' }}>C TYPE</th>
                          <th>PCS</th>
                          <th>T WEIGHT</th>
                          <th>T CBM</th>
                        </tr>
                      </thead>
                      <tbody>
                        {packingRows.map((r) => (
                          <tr key={r.key}>
                            {r.isConsolidatedLayout ? (
                              <>
                                {r.isFirstInMaster ? (
                                  <td
                                    rowSpan={r.masterGroupSize}
                                    className="jet-packing-num"
                                    style={{ verticalAlign: 'top' }}
                                  >
                                    {r.collectionNo}
                                  </td>
                                ) : null}
                                <td>{r.subNo}</td>
                                <td>{r.length || '—'}</td>
                                <td>{r.width || '—'}</td>
                                <td>{r.height || '—'}</td>
                                <td>{r.kg || '—'}</td>
                                <td>{r.cbm || '—'}</td>
                                <td className="jet-packing-desc">{r.cargoType || '—'}</td>
                                <td className="jet-packing-desc">{r.description || '—'}</td>
                                {r.isFirstInD2dGroup ? (
                                  <td
                                    rowSpan={r.d2dGroupSize}
                                    className="jet-packing-num"
                                    style={{ verticalAlign: 'top' }}
                                  >
                                    {r.d2d || '—'}
                                  </td>
                                ) : null}
                                {r.isFirstInD2dGroup ? (
                                  <td
                                    rowSpan={r.d2dGroupSize}
                                    className="jet-packing-desc jet-packing-num"
                                    style={{ textAlign: 'center', verticalAlign: 'top' }}
                                  >
                                    {r.clearingType || '—'}
                                  </td>
                                ) : null}
                                {r.isFirstInMaster ? (
                                  <td
                                    rowSpan={r.masterGroupSize}
                                    className="jet-packing-num"
                                    style={{ verticalAlign: 'top' }}
                                  >
                                    {r.pcs}
                                  </td>
                                ) : null}
                                {r.isFirstInMaster ? (
                                  <td rowSpan={r.masterGroupSize} className="jet-packing-num" style={{ verticalAlign: 'top' }}>
                                    {r.fWeight}
                                  </td>
                                ) : null}
                                {r.isFirstInMaster ? (
                                  <td rowSpan={r.masterGroupSize} className="jet-packing-num" style={{ verticalAlign: 'top' }}>
                                    {r.fCbm}
                                  </td>
                                ) : null}
                              </>
                            ) : (
                              <>
                            {r.isFirstInGroup ? (
                              <td
                                rowSpan={r.groupSize}
                                className="jet-packing-num"
                                style={{ verticalAlign: 'top' }}
                              >
                                {r.collectionNo}
                              </td>
                            ) : null}
                            <td>{r.subNo}</td>
                            <td>{r.length || '—'}</td>
                            <td>{r.width || '—'}</td>
                            <td>{r.height || '—'}</td>
                            <td>{r.kg || '—'}</td>
                            <td>{r.cbm || '—'}</td>
                            <td className="jet-packing-desc">{r.cargoType || '—'}</td>
                            <td className="jet-packing-desc">{r.description || '—'}</td>
                            <td>{r.d2d || '—'}</td>
                            {r.isFirstInGroup ? (
                              <td
                                rowSpan={r.groupSize}
                                className="jet-packing-desc jet-packing-num"
                                style={{ textAlign: 'center', verticalAlign: 'top' }}
                              >
                                {r.clearingType || '—'}
                              </td>
                            ) : null}
                            {r.isFirstInGroup ? (
                              <td
                                rowSpan={r.groupSize}
                                className="jet-packing-num"
                                style={{ verticalAlign: 'top' }}
                              >
                                {r.pcs}
                              </td>
                            ) : null}
                            {r.isFirstInGroup ? (
                              <td rowSpan={r.groupSize} className="jet-packing-num" style={{ verticalAlign: 'top' }}>
                                {r.fWeight}
                              </td>
                            ) : null}
                            {r.isFirstInGroup ? (
                              <td rowSpan={r.groupSize} className="jet-packing-num" style={{ verticalAlign: 'top' }}>
                                {r.fCbm}
                              </td>
                            ) : null}
                              </>
                            )}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {panelTab === 'collections' && (
              <div style={{ marginTop: '1.25rem' }}>
                <p style={{ color: 'var(--jet-muted)', fontSize: '0.875rem', marginTop: 0 }}>
                  Link cargo collections that belong on this shipment. Save shipment details to persist.
                </p>
                {loadingCollections ? (
                  <p style={{ color: 'var(--jet-muted)' }}>Loading collections…</p>
                ) : (
                  <>
                    <div
                      className="jet-admin-filter-row"
                      style={{ flexWrap: 'wrap', gap: '0.75rem', marginBottom: '0.75rem', alignItems: 'flex-end' }}
                    >
                      <div className="jet-admin-filter-search" style={{ flex: '1 1 260px', minWidth: '220px' }}>
                        <Search
                          className="w-4 h-4"
                          style={{
                            position: 'absolute',
                            left: '12px',
                            top: '50%',
                            transform: 'translateY(-50%)',
                            color: 'var(--jet-muted)',
                            pointerEvents: 'none',
                          }}
                        />
                        <input
                          type="search"
                          className="form-input"
                          placeholder="Search collection no., consignee ID, passport, clearing…"
                          value={linkCollectionsSearch}
                          onChange={(e) => setLinkCollectionsSearch(e.target.value)}
                          style={{ paddingLeft: '40px' }}
                          aria-label="Search collections to link"
                        />
                      </div>
                      <div style={{ minWidth: 'min(100%, 220px)' }}>
                        <label className="jet-shipment-label" style={{ display: 'block', marginBottom: '0.25rem' }}>
                          Assignment
                        </label>
                        <select
                          className="form-input"
                          value={linkCollectionsAssignment}
                          onChange={(e) => setLinkCollectionsAssignment(e.target.value)}
                          aria-label="Filter by shipment assignment"
                        >
                          <option value="all">All collections</option>
                          <option value="unassigned">Not assigned to any shipment</option>
                          <option value="on_this">On this shipment only</option>
                          <option value="other_shipment">On another shipment</option>
                        </select>
                      </div>
                      <div style={{ minWidth: 'min(100%, 160px)' }}>
                        <label className="jet-shipment-label" style={{ display: 'block', marginBottom: '0.25rem' }}>
                          Clearing type
                        </label>
                        <select
                          className="form-input"
                          value={linkCollectionsClearing}
                          onChange={(e) => setLinkCollectionsClearing(e.target.value)}
                          aria-label="Filter by clearing type"
                        >
                          <option value="all">All types</option>
                          <option value="gift">Gift</option>
                          <option value="door_to_door">Door to door</option>
                          <option value="upb">UPB</option>
                        </select>
                      </div>
                    </div>
                    <p style={{ margin: '0 0 0.75rem', fontSize: '0.75rem', color: 'var(--jet-muted)' }}>
                      Showing {pickableCollections.length} of {(collections || []).length} collections.
                      {linkCollectionsAssignment === 'unassigned'
                        ? ' Rows already linked to this shipment stay visible so you can adjust the selection.'
                        : null}
                    </p>
                    <div className="jet-shipment-collections-list">
                      {pickableCollections.length === 0 ? (
                        <p style={{ color: 'var(--jet-muted)', margin: 0 }}>No collections match the current search and filters.</p>
                      ) : (
                        pickableCollections.map((c) => {
                          const id = String(c._id || c.id);
                          const checked = draft.linkedCollections.map(String).includes(id);
                          const onOther = collectionIdsLinkedOtherShipments.has(id);
                          return (
                            <label key={id} className="jet-shipment-collection-row">
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={() => toggleCollection(id)}
                              />
                              <span style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', gap: '0.35rem' }}>
                                <strong>{c.collectionNo || id}</strong>
                                <span style={{ color: 'var(--jet-muted)' }}>
                                  {c.clearingType || '—'} · {c.consigneeId || '—'}
                                </span>
                                {onOther ? (
                                  <span
                                    style={{
                                      fontSize: '0.7rem',
                                      fontWeight: 600,
                                      color: '#b45309',
                                      background: '#fffbeb',
                                      padding: '0.1rem 0.35rem',
                                      borderRadius: '4px',
                                    }}
                                  >
                                    Also on another shipment
                                  </span>
                                ) : null}
                                {Array.isArray(c.childCollectionIds) && c.childCollectionIds.length > 0 ? (
                                  <span
                                    style={{
                                      fontSize: '0.7rem',
                                      fontWeight: 600,
                                      color: '#1d4ed8',
                                      background: '#eff6ff',
                                      padding: '0.1rem 0.35rem',
                                      borderRadius: '4px',
                                    }}
                                  >
                                    Consolidated ({c.childCollectionIds.length} D2D)
                                  </span>
                                ) : null}
                              </span>
                            </label>
                          );
                        })
                      )}
                    </div>
                  </>
                )}
                {draft.linkedCollections.length > 0 && (
                  <div style={{ marginTop: '1rem', fontSize: '0.85rem', color: 'var(--jet-muted)' }}>
                    Selected:{' '}
                    {draft.linkedCollections.map((id) => collectionById.get(String(id))?.collectionNo || id).join(', ')}
                  </div>
                )}
              </div>
            )}

            {panelTab === 'tracking' && (
              <div style={{ marginTop: '1.25rem', maxWidth: 520 }}>
                <h3 style={{ fontSize: '0.95rem', fontWeight: 600, margin: '0 0 0.5rem', color: 'var(--jet-text)' }}>
                  Bulk HBL tracking update
                </h3>
                <p style={{ fontSize: '0.875rem', color: 'var(--jet-muted)', margin: '0 0 1rem', lineHeight: 1.5 }}>
                  Apply one status to every package on all HBL collections linked to this shipment.
                  Set the date and time when this step happened — you can enter past dates if updating late.
                </p>

                {editingId === 'new' ? (
                  <p style={{ color: 'var(--jet-muted)', fontSize: '0.875rem' }}>Save the shipment before updating tracking.</p>
                ) : !draft.linkedCollections.length ? (
                  <p style={{ color: 'var(--jet-muted)', fontSize: '0.875rem' }}>
                    Link HBL collections on the Collections tab first.
                  </p>
                ) : (
                  <>
                    <p style={{ fontSize: '0.8125rem', color: 'var(--jet-text)', marginBottom: '1rem' }}>
                      <strong>{draft.linkedCollections.length}</strong> linked HBL(s):{' '}
                      {draft.linkedCollections.map((id) => collectionById.get(String(id))?.collectionNo || id).join(', ')}
                    </p>

                    <label className="jet-shipment-label" style={{ display: 'block', marginBottom: '0.75rem' }}>
                      Status
                      <select
                        className="form-input"
                        style={{ marginTop: '0.35rem' }}
                        value={trackStatus}
                        onChange={(e) => setTrackStatus(e.target.value)}
                      >
                        {TRACKING_STATUS_OPTIONS.map((s) => (
                          <option key={s.value} value={s.value}>{s.label}</option>
                        ))}
                      </select>
                    </label>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginBottom: '0.75rem' }}>
                      <label className="jet-shipment-label" style={{ display: 'block' }}>
                        Date
                        <input
                          type="date"
                          className="form-input"
                          style={{ marginTop: '0.35rem' }}
                          value={trackDate}
                          onChange={(e) => setTrackDate(e.target.value)}
                          required
                        />
                      </label>
                      <label className="jet-shipment-label" style={{ display: 'block' }}>
                        Time
                        <input
                          type="time"
                          className="form-input"
                          style={{ marginTop: '0.35rem' }}
                          value={trackTime}
                          onChange={(e) => setTrackTime(e.target.value)}
                          required
                        />
                      </label>
                    </div>

                    <label className="jet-shipment-label" style={{ display: 'block', marginBottom: '1rem' }}>
                      Note (optional)
                      <input
                        className="form-input"
                        style={{ marginTop: '0.35rem' }}
                        value={trackDescription}
                        onChange={(e) => setTrackDescription(e.target.value)}
                        placeholder="Optional note for the timeline"
                      />
                    </label>

                    <button
                      type="button"
                      className="jet-btn jet-btn-primary"
                      onClick={handleBulkTracking}
                      disabled={trackBusy}
                    >
                      {trackBusy ? 'Updating…' : 'Update all linked HBLs'}
                    </button>

                    {trackMsg ? (
                      <p style={{
                        marginTop: '0.75rem',
                        fontSize: '0.875rem',
                        color: trackMsg.includes('failed') || trackMsg.includes('Failed') ? '#b91c1c' : '#047857',
                      }}
                      >
                        {trackMsg}
                      </p>
                    ) : null}
                  </>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
